import React, { useState, useEffect } from "react";
import { Box, Typography, Checkbox, FormControlLabel } from "@mui/material";

import BillingContactForm from "./BillingContactForm";
import PaymentMethodSection from "./PaymentMethodSection";
import { useAppState } from "../../../context/StateContext";
import BillingService from "../../../services/billing.service";
import useOnBoardingContext from "../../../hooks/useOnBoardingContext";

interface PaymentMethod {
  id: number;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

interface MyBillingProps {
  onReadyChange?: (ready: boolean) => void;
}

const MyBilling: React.FC<MyBillingProps> = ({ onReadyChange }) => {
  const { currentUser } = useAppState();
  const { 
    billingContact, 
    setBillingContact, 
    useAdminDetails, 
    setUseAdminDetails 
  } = useOnBoardingContext();
  
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (useAdminDetails && currentUser) {
      const adminContact = {
        name: currentUser.name || "",
        email: currentUser.email || "",
        phone: currentUser.phone || "",
        address: currentUser.company?.location?.longAddress || "",
      };
      setBillingContact(adminContact);
    } else if (!useAdminDetails && billingContact.name === currentUser?.name) {
      setBillingContact({
        name: "",
        email: "",
        phone: "",
        address: "",
      });
    }
  }, [useAdminDetails, currentUser]);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    // Considera válido se tiver um método de pagamento real OU um método alternativo selecionado
    const hasPaymentMethod = paymentMethods.length > 0;
    const hasAlternativeMethod = selectedPaymentMethod === "direct_debit" || selectedPaymentMethod === "invoice";
    const hasContactInfo = billingContact.name && billingContact.email && billingContact.phone && billingContact.address;
    const isReady = (hasPaymentMethod || hasAlternativeMethod) && !!hasContactInfo;
    
    if (onReadyChange) {
      onReadyChange(isReady);
    }
  }, [paymentMethods, selectedPaymentMethod, billingContact, onReadyChange]);

  const loadPaymentMethods = async () => {
    if (!currentUser?.companyId) {
      return;
    }

    try {
      setLoading(true);
      const data = await BillingService.listPaymentMethods(currentUser.companyId);
      setPaymentMethods(data);
    } catch (error: any) {
      if (error?.response?.data?.error === "CUSTOMER_NOT_FOUND" || error?.response?.status === 404) {
        try {
          const companyData = {
            id: currentUser.companyId,
            name: currentUser.company?.name || "",
            email: currentUser.company?.email || currentUser.email,
            profileId: currentUser.company?.profileId || 0,
          };
          
          const result = await BillingService.createCustomer(companyData, currentUser.id);
          setPaymentMethods([]);
        } catch (createError: any) {
          setPaymentMethods([]);
        }
      } else {
        setPaymentMethods([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodAdded = (paymentMethodData?: any) => {
    if (paymentMethodData) {
      setPaymentMethods([paymentMethodData]);
      // Limpar método alternativo quando um cartão é adicionado
      setSelectedPaymentMethod("");
    }
    loadPaymentMethods();
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontFamily: "Manrope",
            fontSize: "16px",
            lineHeight: "24px",
            color: "#475467",
            mb: 2,
          }}
        >
          Shipeezi is <strong>completely free</strong> to use to receive tasks and operate them on behalf of your agents.
          You can also register and use our shared network feature at no cost.
        </Typography>
        <Typography
          sx={{
            fontFamily: "Manrope",
            fontSize: "16px",
            lineHeight: "24px",
            color: "#475467",
          }}
        >
          Charges will be applied only for any new files you create in the app, including Quotes, Orders, Storage files and Shipments.
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={useAdminDetails}
              onChange={(e) => setUseAdminDetails(e.target.checked)}
              sx={{
                color: "#D0D5DD",
                "&.Mui-checked": {
                  color: "#0055A4",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontSize: "14px",
                lineHeight: "20px",
                color: "#344054",
              }}
            >
              Use the same "administrator user" details
            </Typography>
          }
        />
      </Box>

      <BillingContactForm contact={billingContact} setContact={setBillingContact} />

      <PaymentMethodSection
        paymentMethods={paymentMethods}
        onPaymentMethodAdded={handlePaymentMethodAdded}
        onPaymentMethodSelected={(method) => setSelectedPaymentMethod(method || "")}
        companyId={currentUser?.companyId || 0}
      />
    </Box>
  );
};

export default MyBilling;